// The coin catalogue as pure data: which token × chain pairs the wallet
// offers, and how each is labelled. No icons, and that is the whole point.
//
// This list is the thing a new chain has to be added to, so it is the thing
// worth testing — and the test beside it checks it against the server's own
// isValidChainToken and TOKENS, in both directions. It caught a missing TON
// row exactly once and would have caught the next one too.
//
// It stopped catching anything the moment the icons became real images: the
// test imports this list, the list reached the icon components, and those now
// import .webp files the test runner cannot load. The whole file failed to
// import and the suite reported one dead test instead of a broken catalogue.
// Splitting the rows from the artwork is what keeps the guard alive — and it
// is what the original file said it was for.
//
// `coins.ts` re-attaches the icons for the UI; nothing else should import
// from here.

// is labelled.
//
// This lived inside TokenSheet.tsx, which drags in hooks and image assets, so
// the one piece of the file that is pure data — and the piece a new chain has
// to be added to — could not be tested without a bundler. The test beside this
// file checks the list against the server's own isValidChainToken and TOKENS,
// in both directions.

import type { AnyTokenSymbol, Chain } from '@incpt/shared';

/** Re-exported rather than re-declared: a second copy of the token union had
 *  to be edited for every new coin, and could drift from the shared one. */
export type CoinSymbol = AnyTokenSymbol;

export type CoinRow = {
  name: string;
  symbol: CoinSymbol;
  network: string;
  chain: Chain;
  /** Native chain coin: network fee paid in-kind, excluded from card flows. */
  native?: boolean;
  /**
   * Whether the swap provider can trade this coin. False keeps it out of the
   * swap screen while receive and send still work — the provider's ticker for
   * a network has to be read off its live currency list, never guessed: BNB
   * exists there as both BEP-2 and BEP-20, and paying out the wrong one loses
   * the funds.
   */
  swappable?: boolean;
  /**
   * Whether a send can actually go out on this chain. False keeps it out of
   * the send screen while receiving still works — a chain whose send path the
   * API refuses would otherwise be offered and then fail at submit, which
   * reads as a bug rather than as "not yet".
   */
  sendable?: boolean;
};

// Stablecoins exist on each contract chain (token × chain); a coin is
// single-chain. All combos are validated server-side (isValidChainToken), and
// the test beside this file checks the list against it both ways — a combo
// listed here that the server rejects would be an unsendable row in the UI.
export const COIN_ROWS: CoinRow[] = [
  { name: 'Tether', symbol: 'USDT', network: 'TRC-20', chain: 'trx' },
  { name: 'Tether', symbol: 'USDT', network: 'ERC-20', chain: 'eth' },
  { name: 'Tether', symbol: 'USDT', network: 'BEP-20', chain: 'bsc' },
  { name: 'USD Coin', symbol: 'USDC', network: 'ERC-20', chain: 'eth' },
  { name: 'USD Coin', symbol: 'USDC', network: 'TRC-20', chain: 'trx' },
  { name: 'USD Coin', symbol: 'USDC', network: 'BEP-20', chain: 'bsc' },
  { name: 'Bitcoin', symbol: 'BTC', network: 'Bitcoin', chain: 'btc', native: true },
  { name: 'Ethereum', symbol: 'ETH', network: 'Ethereum', chain: 'eth', native: true },
  { name: 'TRON', symbol: 'TRX', network: 'Tron', chain: 'trx', native: true },
  {
    name: 'Tether',
    symbol: 'USDT',
    network: 'Solana',
    chain: 'sol',
  },
  {
    name: 'USD Coin',
    symbol: 'USDC',
    network: 'Solana',
    chain: 'sol',
  },
  {
    name: 'Solana',
    symbol: 'SOL',
    network: 'Solana',
    chain: 'sol',
    native: true,
  },
  {
    // Tether's omnichain USD₮0 on this chain, surfaced under our USDT symbol
    // the same way Binance-Peg USDT is on BSC. The network label says which
    // chain it lives on. Not on ChangeHero — keep out of swap.
    name: 'Tether',
    symbol: 'USDT',
    network: 'HyperEVM',
    chain: 'hyperevm',
    swappable: false,
  },
  {
    name: 'USD Coin',
    symbol: 'USDC',
    network: 'HyperEVM',
    chain: 'hyperevm',
    swappable: false,
  },
  {
    name: 'Hyperliquid',
    symbol: 'HYPE',
    network: 'HyperEVM',
    chain: 'hyperevm',
    native: true,
    swappable: false,
  },
  {
    // Tether's jetton on TON, under our USDT symbol like the other chains'
    // variants. TON carries no USDC worth accepting, so there is no USDC row
    // here — and the test beside this file checks that against the server's
    // token table rather than trusting the omission.
    name: 'Tether',
    symbol: 'USDT',
    network: 'TON',
    chain: 'ton',
  },
  {
    name: 'Toncoin',
    symbol: 'TON',
    network: 'TON',
    chain: 'ton',
    native: true,
  },
  {
    // The balance for this row does not come from the server: Monero hides
    // amounts and has no address index, so only the device can compute it by
    // scanning with the view key. The wallet shows a sync state here that no
    // other coin has.
    // Not swappable yet: provider pay-ins need a payment ID / integrated
    // address our send path cannot attach.
    name: 'Monero',
    symbol: 'XMR',
    network: 'Monero',
    chain: 'xmr',
    native: true,
    swappable: false,
  },
  {
    // Transparent ZEC only — we neither derive nor spend shielded addresses,
    // so a user's shielded balance is invisible here by construction.
    name: 'Zcash',
    symbol: 'ZEC',
    network: 'Zcash',
    chain: 'zec',
    native: true,
  },
  {
    name: 'BNB',
    symbol: 'BNB',
    network: 'BNB Smart Chain',
    chain: 'bsc',
    native: true,
  },
  {
    // Swappable is left at its default (true): unlike Monero, Dogecoin needs
    // no payment ID, so a provider pay-in is an ordinary address. Whether the
    // provider actually lists it is answered by its own currency feed, and a
    // coin it does not list simply never appears.
    name: 'Dogecoin',
    symbol: 'DOGE',
    network: 'Dogecoin',
    chain: 'doge',
    native: true,
  },
  {
    // The balance shown for this row is what the account HOLDS. What can be
    // sent is less: an XRPL account must keep a reserve to exist, so the send
    // screen asks the API for the spendable figure rather than subtracting
    // here — the reserve is a ledger parameter that changes by validator vote.
    //
    // Not swappable yet: the provider's XRP pay-ins want a destination tag,
    // and our send path cannot attach one. Same reason Monero is excluded.
    name: 'XRP',
    symbol: 'XRP',
    network: 'XRP Ledger',
    chain: 'xrp',
    native: true,
    swappable: false,
  },
];

/** Coins the swap screen may offer. */
export const SWAPPABLE_COIN_ROWS: CoinRow[] = COIN_ROWS.filter((c) => c.swappable !== false);

/** Coins the send screen may offer. */
export const SENDABLE_COIN_ROWS: CoinRow[] = COIN_ROWS.filter((c) => c.sendable !== false);

/** Stable key for a token × chain coin. */
export const coinKey = (c: Pick<CoinRow, 'symbol' | 'chain'>) => `${c.symbol}-${c.chain}`;

/** Coins on a chain, or all when `chain` is undefined. */
export const coinRowsForChain = (chain?: CoinRow['chain']) =>
  chain ? COIN_ROWS.filter((c) => c.chain === chain) : COIN_ROWS;

/** Unique coins by symbol (first of each — no network duplicate). */
export const UNIQUE_COIN_ROWS: CoinRow[] = COIN_ROWS.filter(
  (c, _, arr) => arr.findIndex((x) => x.symbol === c.symbol) === arr.indexOf(c),
);

/** Always-defined default coin (USDT on Tron, the canonical USDT network).
 *  Order-independent lookup so reordering COIN_ROWS can't change the default. */
export const DEFAULT_COIN_ROW: CoinRow = COIN_ROWS.find((c) => c.symbol === 'USDT' && c.chain === 'trx')!;
